// Generic PDP upsell. Renders empty, then pulls its content from the
// recommendations endpoint. Not Dawn's <product-recommendations>, which grabs the
// first match in the response and would clash with a `complementary` block.
class ProductUpsell extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    if (this.dataset.state === 'loaded') {
      this.initCarousel();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.load();
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
  }

  get requestUrl() {
    const { url, productId, sectionId } = this.dataset;
    return `${url}&product_id=${productId}&section_id=${sectionId}`;
  }

  load() {
    fetch(this.requestUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('product-upsell');

        if (!fresh || fresh.innerHTML.trim().length === 0) {
          this.remove();
          return;
        }

        this.innerHTML = fresh.innerHTML;
        this.dataset.state = 'loaded';
        this.classList.add('product-upsell--loaded');
        this.initCarousel();
      })
      .catch((error) => {
        console.error('[product-upsell]', error);
        this.remove();
      });
  }

  initCarousel() {
    this.track = this.querySelector('[data-upsell-track]');
    this.arrows = this.querySelector('[data-upsell-arrows]');
    this.prevButton = this.querySelector('[data-upsell-prev]');
    this.nextButton = this.querySelector('[data-upsell-next]');

    if (!this.track) return;

    this.prevButton?.addEventListener('click', () => this.scrollByPage(-1));
    this.nextButton?.addEventListener('click', () => this.scrollByPage(1));
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);

    this.updateArrows();
  }

  get step() {
    const first = this.track?.querySelector('.product-upsell__item');
    if (!first) return 200;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  scrollByPage(direction) {
    this.track?.scrollBy({ left: this.step * direction, behavior: 'smooth' });
  }

  onScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateArrows();
    });
  }

  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.updateArrows(), 150);
  }

  updateArrows() {
    if (!this.track || !this.arrows) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    // 2px tolerance: fractional widths never reach the exact bound.
    const isScrollable = scrollWidth - clientWidth > 2;

    this.arrows.hidden = !isScrollable;
    if (!isScrollable) return;

    if (this.prevButton) this.prevButton.disabled = scrollLeft <= 2;
    if (this.nextButton) this.nextButton.disabled = scrollLeft >= scrollWidth - clientWidth - 2;
  }
}

if (!customElements.get('product-upsell')) {
  customElements.define('product-upsell', ProductUpsell);
}
